/** Reports surface — source-backed sales, settlement activity and payout evidence. */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { AppShell } from '../../components/AppShell';
import { CompleteMonthRangeControls } from '../../components/CompleteMonthRangeControls';
import { useMe, useModuleEnabled } from '../../lib/auth';
import {
  defaultFinancePeriod,
  financeCategoryDirection,
  financeCategoryLabel,
  financeCoverageCopy,
  financeAccountingCoverageCopy,
  financeDateBasisLabel,
  financeIncomeReason,
  financePayoutReconciliationCopy,
  financePayoutStatusLabel,
  financeSettlementHeaderCopy,
  formatFinanceCurrency,
  formatFinanceDelta,
  formatFinanceSourceCost,
  formatFinanceSourceUnits,
  financeSourceCostLabel,
  financeSourceEventLabel,
  financeWarehouseLinkCopy,
  payoutDateCopy,
  transitionFinancePeriod,
  useFinanceSummary,
  type FinancePeriod,
} from '../../lib/finance-summary';
import { Badge, Card } from '../../design';
import { SkeletonLine } from '../../components/Skeleton';
import { formatFeeCenterDate } from '../../lib/fee-center';
import type {
  FinanceSummaryPayout,
  FinanceSummaryResponse,
  FinanceSummarySettlementActivity,
  FinanceSummarySource,
} from '@sourcito/schemas';

function periodLabel(period: FinancePeriod): string {
  return `${formatFeeCenterDate(`${period.from}T00:00:00Z`)} – ${formatFeeCenterDate(`${period.to}T00:00:00Z`)}`;
}

export function ReportsPage() {
  const { data: me, isLoading: meLoading } = useMe();
  const amazonEnabled = useModuleEnabled('amazon');
  const [period, setPeriod] = useState<FinancePeriod>(() => defaultFinancePeriod());
  const [periodError, setPeriodError] = useState<string | null>(null);
  const hasTenantAccess =
    !meLoading && !!me && (me.user.role === 'owner' || me.user.role === 'operator');
  const query = useFinanceSummary(period, hasTenantAccess && amazonEnabled);

  if (meLoading || !me) return <AccessLoading />;
  if (me.user.role === 'warehouse') return <AccessDenied />;
  if (me.user.role !== 'owner' && me.user.role !== 'operator') return <AccessDenied />;

  return (
    <AppShell>
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 py-6">
          <div className="flex items-start justify-between gap-5 flex-wrap">
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-fg-tertiary">REPORTS</p>
              <h1 className="font-display text-4xl sm:text-[44px] font-medium leading-[1.08] tracking-tight mt-3">
                The record.
              </h1>
              <p className="text-sm text-fg-secondary mt-3 max-w-2xl">
                Period views of sales, settlement activity, cash evidence, and source-backed trends.
                Each measure keeps its own currency and date basis.
              </p>
            </div>
            <CompleteMonthRangeControls
              period={period}
              error={periodError}
              labelPrefix="Reports"
              onChange={(field, value) => {
                const next = transitionFinancePeriod(period, field, value);
                setPeriodError(next.error);
                if (!next.error) setPeriod(next.period);
              }}
            />
          </div>
          <p className="text-xs text-fg-tertiary mt-4">Showing {periodLabel(period)} (UTC).</p>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 sm:px-8 py-8 space-y-8">
        {!amazonEnabled && <AmazonDisabled />}
        {amazonEnabled && query.isError && (
          <Card>
            <p className="text-sm text-negative">
              Could not load Reports:{' '}
              {query.error instanceof Error ? query.error.message : 'request failed'}
            </p>
            <p className="text-xs text-fg-tertiary mt-1">
              Try another complete UTC calendar-month period.
            </p>
          </Card>
        )}
        {amazonEnabled && query.isLoading && (
          <>
            <p className="sr-only" aria-live="polite">
              Loading Reports…
            </p>
            <ReportsSkeleton />
          </>
        )}
        {amazonEnabled && query.data && <ReportContent data={query.data} />}
      </main>
    </AppShell>
  );
}

// Coarse shimmer for ReportContent's section list (sales, income, cash
// flow, expenses, trends, recent transactions) — every section shares the
// same "heading + detail line, then a card with a small month-row table"
// shape, so one repeated block stands in for all six rather than mirroring
// each section's own sub-tables exactly.
function ReportsSkeleton() {
  return (
    <div className="space-y-8" aria-hidden="true">
      {[0, 1, 2, 3].map((section) => (
        <div key={section}>
          <SkeletonLine className="h-5 w-40 rounded-xs" />
          <SkeletonLine className="h-3.5 w-72 rounded-xs mt-2" />
          <Card className="mt-3">
            <SkeletonLine className="h-4 w-48" />
            <div className="mt-5 space-y-3">
              <SkeletonLine className="h-3 w-full" />
              <SkeletonLine className="h-3 w-full" />
              <SkeletonLine className="h-3 w-full" />
              <SkeletonLine className="h-3 w-3/4" />
            </div>
          </Card>
        </div>
      ))}
    </div>
  );
}

function ReportContent({ data }: { data: FinanceSummaryResponse }) {
  const settlementCopy = financeCoverageCopy(data.coverage.settlements, 'settlement');
  const salesCopy = financeCoverageCopy(data.coverage.sales, 'sales');
  return (
    <>
      {(settlementCopy || salesCopy) && (
        <div className="grid gap-3 sm:grid-cols-2" aria-label="Report source coverage">
          {settlementCopy && <CoverageBanner copy={settlementCopy} />}
          {salesCopy && <CoverageBanner copy={salesCopy} />}
        </div>
      )}
      <SalesSection data={data} />
      <IncomeSection data={data} />
      <CashFlowSection data={data} />
      <ExpensesSection activities={data.settlementActivity} />
      <TrendsSection data={data} />
      <RecentTransactions />
    </>
  );
}

function SalesSection({ data }: { data: FinanceSummaryResponse }) {
  return (
    <section aria-labelledby="reports-sales-heading">
      <SectionHeading
        id="reports-sales-heading"
        title="Total sales"
        detail="Units and Amazon sales-report revenue by purchase date · UTC. Separate currencies are never combined."
      />
      {data.sales.length === 0 ? (
        <EmptyCard
          text="No sales rows were found for this period."
          coverage={data.coverage.sales.complete}
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {data.sales.map((sales) => (
            <Card key={sales.currency}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <h3 className="text-base font-medium text-fg-primary">Sales · {sales.currency}</h3>
                <Badge variant="info">Purchase date · UTC</Badge>
              </div>
              <ReportMonthRows
                rows={sales.months}
                columns={['Month', 'Units', 'Revenue']}
                render={(month) => [
                  month.month,
                  month.units == null ? '—' : `${month.units} units`,
                  formatFinanceCurrency(month.revenueCents, sales.currency),
                ]}
              />
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}

function IncomeSection({ data }: { data: FinanceSummaryResponse }) {
  const activities = data.settlementActivity;
  const cogsCoverage = financeAccountingCoverageCopy(data.coverage.cogs, 'cogs');
  const warehouseCoverage = financeAccountingCoverageCopy(
    data.coverage.warehouseExpenses,
    'warehouseExpenses'
  );
  return (
    <section aria-labelledby="reports-income-heading">
      <SectionHeading
        id="reports-income-heading"
        title="Income"
        detail="Server-recorded settlement revenue, Amazon charges and credits, landed COGS, warehouse expense, and net income. Settlement revenue, Amazon charges, and proceeds use settlement posted date · UTC; landed COGS uses sale purchase date / COGS month; warehouse expense uses recognized date · UTC; net income combines these date bases. Each currency remains separate."
      />
      {(cogsCoverage.status !== 'complete' || warehouseCoverage.status !== 'complete') && (
        <div className="grid gap-3 sm:grid-cols-2 mb-4" aria-label="Reports accounting coverage">
          {cogsCoverage.status !== 'complete' && <CoverageBanner copy={cogsCoverage} />}
          {warehouseCoverage.status !== 'complete' && <CoverageBanner copy={warehouseCoverage} />}
        </div>
      )}
      {activities.length === 0 ? (
        <EmptyCard text="No settlement activity was found for this period." />
      ) : (
        <div className="grid gap-4 grid-cols-1">
          {activities.map((activity) => (
            <div key={activity.currency} className="min-w-0 w-full">
              <IncomeActivityCard
                activity={activity}
                cogsCoverage={cogsCoverage}
                warehouseCoverage={warehouseCoverage}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function IncomeActivityCard({
  activity,
  cogsCoverage,
  warehouseCoverage,
}: {
  activity: FinanceSummarySettlementActivity;
  cogsCoverage: ReturnType<typeof financeAccountingCoverageCopy>;
  warehouseCoverage: ReturnType<typeof financeAccountingCoverageCopy>;
}) {
  const cogsAvailable = cogsCoverage.allowsOfficialPeriodTotal && activity.landedCogsCents != null;
  const warehouseAvailable =
    warehouseCoverage.allowsOfficialPeriodTotal && activity.warehousePeriodExpenseCents != null;
  const incomeAvailable =
    cogsAvailable &&
    warehouseAvailable &&
    activity.incomeStatus === 'complete' &&
    activity.netIncomeCents != null;
  const incomeReason = financeIncomeReason(activity.incomeStatus, activity.incomeBlockedReason);
  return (
    <Card>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h3 className="text-base font-medium text-fg-primary">
          Settlement income · {activity.currency}
        </h3>
      </div>
      <p className="text-xs text-fg-tertiary mt-2">
        Settlement revenue, Amazon charges, and proceeds: settlement posted date · UTC. Landed COGS:
        sale purchase date / COGS month. Warehouse expense: recognized date · UTC. Net income
        combines these date bases.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <Metric
          label="Settlement revenue"
          value={formatFinanceDelta(activity.settlementRevenueCents, activity.currency)}
        />
        <Metric
          label="Amazon charges / credits"
          value={formatFinanceDelta(activity.amazonNetChargeCents, activity.currency)}
        />
        <Metric
          label="Proceeds before COGS"
          value={formatFinanceDelta(activity.netProceedsBeforeCogsCents, activity.currency)}
        />
      </div>
      <div className="mt-5 rounded-lg bg-surface-elev p-4">
        <AccountingFact
          label="Landed COGS"
          value={
            cogsAvailable ? formatFinanceCurrency(activity.landedCogsCents, activity.currency) : '—'
          }
          available={cogsAvailable}
          coverageLabel={cogsCoverage.label}
        />
        {!cogsAvailable && (
          <p className="text-xs text-fg-secondary mt-2">
            Official COGS total unavailable until coverage is complete and a server value is
            present.
          </p>
        )}
        <AccountingFact
          label="Warehouse period expense"
          value={
            warehouseAvailable
              ? formatFinanceCurrency(activity.warehousePeriodExpenseCents, activity.currency)
              : '—'
          }
          available={warehouseAvailable}
          coverageLabel={warehouseCoverage.label}
          separated
        />
        <AccountingFact
          label="Net income"
          value={
            incomeAvailable
              ? formatFinanceCurrency(activity.netIncomeCents, activity.currency)
              : '—'
          }
          available={incomeAvailable}
          coverageLabel={incomeAvailable ? 'Complete' : 'Blocked'}
          separated
        />
        {!incomeAvailable && incomeReason && (
          <p className="text-xs text-fg-secondary mt-2">{incomeReason}</p>
        )}
      </div>
      <AccountingMonthRows activity={activity} />
      <CogsProvenance sources={activity.sources} currency={activity.currency} />
    </Card>
  );
}

function AccountingFact({
  label,
  value,
  available,
  coverageLabel,
  separated = false,
}: {
  label: string;
  value: string;
  available: boolean;
  coverageLabel: string;
  separated?: boolean;
}) {
  return (
    <div className={separated ? 'mt-4 pt-4 border-t border-border-subtle' : ''}>
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-fg-primary">{label}</span>
        <span className="flex items-center gap-2">
          <span className="font-display text-lg tabular-nums">{value}</span>
          <Badge variant={available ? 'positive' : 'warning'}>
            {available ? 'Complete' : coverageLabel === 'Complete' ? 'Unavailable' : coverageLabel}
          </Badge>
        </span>
      </div>
    </div>
  );
}

type AccountingMonthColumnKey =
  | 'month'
  | 'revenue'
  | 'charges'
  | 'proceeds'
  | 'cogs'
  | 'warehouseExpense'
  | 'netIncome';

type AccountingMonthColumn = {
  key: AccountingMonthColumnKey;
  label: string;
  value: (month: FinanceSummarySettlementActivity['monthly'][number], currency: string) => string;
};

type AccountingMonthFactKey =
  | 'settlementRevenueCents'
  | 'amazonNetChargeCents'
  | 'netProceedsBeforeCogsCents'
  | 'landedCogsCents'
  | 'warehousePeriodExpenseCents'
  | 'netIncomeCents';

function accountingMonthFact(
  month: FinanceSummarySettlementActivity['monthly'][number],
  key: AccountingMonthFactKey,
  currency: string
): string {
  if (key === 'netIncomeCents') {
    const required: AccountingMonthFactKey[] = [
      'landedCogsCents',
      'warehousePeriodExpenseCents',
      'netIncomeCents',
    ];
    if (required.some((requiredKey) => !Object.prototype.hasOwnProperty.call(month, requiredKey))) {
      return 'Unavailable';
    }
    if (
      month.incomeStatus !== 'complete' ||
      required.some((requiredKey) => month[requiredKey] == null)
    ) {
      return 'Blocked';
    }
  }
  if (!Object.prototype.hasOwnProperty.call(month, key)) return 'Unavailable';
  const value = month[key];
  if (value == null) return 'Blocked';
  return formatFinanceDelta(value, currency);
}

const accountingMonthColumns: AccountingMonthColumn[] = [
  { key: 'month', label: 'Month', value: (month) => month.month },
  {
    key: 'revenue',
    label: 'Revenue',
    value: (month, currency) => accountingMonthFact(month, 'settlementRevenueCents', currency),
  },
  {
    key: 'charges',
    label: 'Charges / credits',
    value: (month, currency) => accountingMonthFact(month, 'amazonNetChargeCents', currency),
  },
  {
    key: 'proceeds',
    label: 'Proceeds before COGS',
    value: (month, currency) => accountingMonthFact(month, 'netProceedsBeforeCogsCents', currency),
  },
  {
    key: 'cogs',
    label: 'Landed COGS',
    value: (month, currency) => accountingMonthFact(month, 'landedCogsCents', currency),
  },
  {
    key: 'warehouseExpense',
    label: 'Warehouse expense',
    value: (month, currency) => accountingMonthFact(month, 'warehousePeriodExpenseCents', currency),
  },
  {
    key: 'netIncome',
    label: 'Net income',
    value: (month, currency) => accountingMonthFact(month, 'netIncomeCents', currency),
  },
];

function AccountingMonthRows({ activity }: { activity: FinanceSummarySettlementActivity }) {
  const gridTemplateColumns = `repeat(${accountingMonthColumns.length}, minmax(0, 1fr))`;
  return (
    <div className="mt-5">
      <p className="text-xs uppercase tracking-[0.15em] text-fg-tertiary">
        Observed monthly accounting facts
      </p>
      <p className="text-xs text-fg-tertiary mt-1">
        Settlement revenue, Amazon charges, and proceeds: settlement posted date · UTC. Landed COGS:
        sale purchase date / COGS month. Warehouse expense: recognized date · UTC. Net income
        combines these date bases. Monthly server values remain independent and are not summed into
        the period total.
      </p>
      <div className="mt-2 min-w-0 overflow-hidden">
        <div className="hidden lg:block min-w-0 divide-y divide-border-subtle">
          <div
            className="grid min-w-0 gap-3 py-2 text-xs text-fg-tertiary"
            style={{ gridTemplateColumns }}
          >
            {accountingMonthColumns.map((column) => (
              <span key={column.key} className="min-w-0 break-words">
                {column.label}
              </span>
            ))}
          </div>
          {activity.monthly.map((month) => (
            <div
              key={month.month}
              className="grid min-w-0 gap-3 py-2 text-sm"
              style={{ gridTemplateColumns }}
            >
              {accountingMonthColumns.map((column) => (
                <span
                  key={`${month.month}-${column.key}`}
                  className={`min-w-0 break-words ${column.key === 'month' ? 'text-fg-secondary' : 'tabular-nums'}`}
                >
                  {column.value(month, activity.currency)}
                </span>
              ))}
            </div>
          ))}
        </div>
        <div className="lg:hidden min-w-0 space-y-2">
          {activity.monthly.map((month) => (
            <div key={month.month} className="min-w-0 rounded-lg bg-surface-elev p-3">
              <div className="mt-2 grid min-w-0 grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {accountingMonthColumns.map((column) => (
                  <span key={column.key} className="contents">
                    <span className="min-w-0 break-words text-fg-tertiary">{column.label}</span>
                    <span className="min-w-0 break-words text-right tabular-nums">
                      {column.value(month, activity.currency)}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function CogsProvenance({
  sources,
  currency,
}: {
  sources: FinanceSummarySource[] | undefined;
  currency: string;
}) {
  return (
    <div className="mt-5" aria-label="COGS provenance">
      <p className="text-xs uppercase tracking-[0.15em] text-fg-tertiary">COGS provenance</p>
      {sources === undefined ? (
        <p className="text-xs text-fg-secondary mt-2">
          COGS evidence is unavailable in legacy data.
        </p>
      ) : sources.length === 0 ? (
        <p className="text-xs text-fg-secondary mt-2">No COGS source rows were recorded.</p>
      ) : (
        <div className="mt-2 space-y-2">
          {sources.map((source, index) => (
            <CogsSourceRow
              key={`${source.consumptionId}-${source.recognizedAt}-${index}`}
              source={source}
              currency={currency}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CogsSourceRow({ source, currency }: { source: FinanceSummarySource; currency: string }) {
  const warehouseLink =
    financeWarehouseLinkCopy(source) ??
    (source.costLayerId ? (source.warehouseAllocationId ?? 'unavailable') : 'unavailable');
  return (
    <details className="rounded-lg bg-surface-elev p-3">
      <summary className="cursor-pointer text-sm text-fg-primary">
        COGS source · {source.saleId}
      </summary>
      <div className="mt-3 space-y-1 text-xs text-fg-secondary">
        <p>Sale ID: {source.saleId}</p>
        <p>Consumption ID: {source.consumptionId}</p>
        <p>Shipment ID: {source.shipmentId}</p>
        <p>Shipment line ID: {source.shipmentLineId}</p>
        <p>FBA allocation ID: {source.fbaAllocationId}</p>
        <p>Lot ID: {source.lotId}</p>
        <p>Cost layer ID: {source.costLayerId ?? '—'}</p>
        <p>Warehouse allocation ID: {source.warehouseAllocationId ?? '—'}</p>
        <p>Charge ID: {source.chargeId ?? '—'}</p>
        <p>Invoice ID: {source.invoiceId ?? '—'}</p>
        <p>Invoice line ID: {source.invoiceLineId ?? '—'}</p>
        <p>Prep batch: {source.prepReference ?? '—'}</p>
        <p>Prep box: {source.prepBoxReference ?? '—'}</p>
        <p>Receipt: {source.receiptId ?? '—'}</p>
        <p>PO: {source.poReference ?? '—'}</p>
        <p>{financeSourceCostLabel(source.costLayerId)}</p>
        <p>Event: {financeSourceEventLabel(source.event)}</p>
        <p>Signed units: {formatFinanceSourceUnits(source.vendorUnits)}</p>
        <p>Signed cost: {formatFinanceSourceCost(source.vendorCostCents, currency)}</p>
        <p>
          {financeDateBasisLabel('purchase_date_utc')}: {formatFeeCenterDate(source.purchaseDate)}
        </p>
        <p>
          {financeDateBasisLabel('recognized_at_utc')}: {source.recognizedAt}
        </p>
        <p>
          {warehouseLink === 'Warehouse link unavailable'
            ? warehouseLink
            : `Warehouse link: ${warehouseLink}`}
        </p>
      </div>
    </details>
  );
}

function CashFlowSection({ data }: { data: FinanceSummaryResponse }) {
  const hasUndated = data.payouts.some((payout) => !payout.depositDate);
  return (
    <section aria-labelledby="reports-cash-heading">
      <SectionHeading
        id="reports-cash-heading"
        title="Cash flow"
        detail="Amazon settlement headers are reported inflows; Reports omits outgoing cash and bank timing."
      />
      {!data.coverage.settlements.complete && (
        <Card className="mb-3">
          <p className="text-sm font-medium text-fg-primary">Settlement totals withheld</p>
          <p className="text-xs text-fg-secondary mt-1">
            The selected source window is incomplete, so a payout subtotal cannot be presented as a
            complete period.
          </p>
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-medium text-fg-primary">Settlement inflows</h3>
            <Badge variant="neutral">Amazon report headers</Badge>
          </div>
          <p className="text-xs text-fg-tertiary mt-2">{financeSettlementHeaderCopy()}</p>
          {!data.coverage.settlements.complete ? (
            <p className="text-sm text-fg-tertiary mt-5">
              Period inflow totals are withheld until settlement coverage is complete.
            </p>
          ) : data.payoutTotals.length === 0 ? (
            <p className="text-sm text-fg-tertiary mt-5">
              No dated settlement inflows in this period.
            </p>
          ) : (
            <div className="mt-5 space-y-3">
              {data.payoutTotals.map((total) => (
                <div key={total.currency} className="flex items-center justify-between gap-3">
                  <span className="text-sm text-fg-secondary">{total.currency}</span>
                  <span className="font-display text-xl tabular-nums">
                    {formatFinanceCurrency(total.totalCents, total.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-medium text-fg-primary">Outgoing cash unavailable</h3>
            <Badge variant="neutral">Unavailable</Badge>
          </div>
          <p className="text-sm text-fg-secondary mt-3">
            Invoice payment history is available per invoice in Finance. Reports does not aggregate
            outgoing cash or bank timing.
          </p>
          {hasUndated && (
            <p className="text-xs text-warning mt-3">
              Undated settlement evidence is shown below and excluded from period totals.
            </p>
          )}
        </Card>
      </div>
      {data.payouts.length > 0 && (
        <div className="mt-4 space-y-3">
          <p className="text-xs uppercase tracking-[0.15em] text-fg-tertiary">
            Settlement evidence
          </p>
          {data.payouts.map((payout) => (
            <PayoutEvidence key={payout.settlementId} payout={payout} />
          ))}
        </div>
      )}
    </section>
  );
}

function PayoutEvidence({ payout }: { payout: FinanceSummaryPayout }) {
  const variant =
    payout.reconciliationStatus === 'reconciled'
      ? 'positive'
      : payout.reconciliationStatus === 'partial'
        ? 'warning'
        : 'neutral';
  return (
    <Card>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="text-sm font-medium text-fg-primary">
            {payout.settlementId} · {payout.currency}
          </h3>
          <p className="text-xs text-fg-tertiary mt-1">{payoutDateCopy(payout.depositDate)}</p>
        </div>
        <Badge variant={variant}>{financePayoutStatusLabel(payout.reconciliationStatus)}</Badge>
      </div>
      <div className="grid gap-4 sm:grid-cols-3 mt-5">
        <Metric
          label="Amazon report header"
          value={formatFinanceCurrency(payout.settlementTotalCents, payout.currency)}
        />
        <Metric
          label="Explained by stored lines"
          value={formatFinanceDelta(payout.explainedLedgerNetCents, payout.currency)}
        />
        <Metric
          label="Header-to-line gap"
          value={formatFinanceDelta(payout.unexplainedDeltaCents, payout.currency)}
        />
      </div>
      <p className="text-xs text-fg-secondary mt-4">
        {financePayoutReconciliationCopy(
          payout.reconciliationStatus,
          payout.unexplainedDeltaCents,
          payout.currency
        )}
      </p>
    </Card>
  );
}

function ExpensesSection({ activities }: { activities: FinanceSummarySettlementActivity[] }) {
  const rows = activities.flatMap((activity) =>
    activity.categories.map((category) => ({ ...category, currency: activity.currency }))
  );
  return (
    <section aria-labelledby="reports-expenses-heading">
      <SectionHeading
        id="reports-expenses-heading"
        title="Expenses"
        detail="Amazon fee category movements by posted month. Credits are shown separately from charges; warehouse expense remains an Income fact."
      />
      {rows.length === 0 ? (
        <EmptyCard text="No Amazon category movements were found for this period." />
      ) : (
        <Card>
          <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-3 pb-2 text-xs text-fg-tertiary border-b border-border-subtle">
            <span>Category</span>
            <span>Currency</span>
            <span>Direction</span>
            <span>Amount</span>
          </div>
          <div className="divide-y divide-border-subtle">
            {rows.map((row) => (
              <div
                key={`${row.currency}-${row.month}-${row.category}`}
                className="py-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-1 sm:gap-3 sm:items-center text-sm"
              >
                <span className="text-fg-secondary">
                  {row.month} · {financeCategoryLabel(row.category)}
                </span>
                <span className="text-xs text-fg-tertiary">{row.currency}</span>
                <span className={row.netChargeCents < 0 ? 'text-positive' : 'text-fg-secondary'}>
                  {financeCategoryDirection(row.netChargeCents)}
                </span>
                <span className="tabular-nums sm:text-right">
                  {formatFinanceDelta(row.netChargeCents, row.currency)}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}

function TrendsSection({ data }: { data: FinanceSummaryResponse }) {
  const currencies = new Set([
    ...data.settlementActivity.map((item) => item.currency),
    ...data.sales.map((item) => item.currency),
  ]);
  return (
    <section aria-labelledby="reports-trends-heading">
      <SectionHeading
        id="reports-trends-heading"
        title="Trends"
        detail="Monthly server measures retain their source, currency, and date basis; Reports does not sum accounting rows."
      />
      {currencies.size === 0 ? (
        <EmptyCard text="No monthly source measures are available for this period." />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {[...currencies].map((currency) => {
            const activity = data.settlementActivity.find((item) => item.currency === currency);
            const sales = data.sales.find((item) => item.currency === currency);
            const months = new Set([
              ...(activity?.monthly.map((item) => item.month) ?? []),
              ...(sales?.months.map((item) => item.month) ?? []),
            ]);
            return (
              <Card key={currency}>
                <h3 className="text-base font-medium text-fg-primary">
                  {currency} monthly measures
                </h3>
                <div className="mt-3 text-xs text-fg-tertiary space-y-1">
                  <p>Sales: purchase date · UTC</p>
                  <p>
                    Settlement revenue, Amazon charges, and proceeds: settlement posted date · UTC
                  </p>
                  <p>COGS: sale purchase date / COGS month</p>
                  <p>Warehouse expense: recognized date · UTC</p>
                  <p>Net income combines these date bases.</p>
                </div>
                <div className="mt-4 space-y-3">
                  {[...months].sort().map((month) => {
                    const sale = sales?.months.find((item) => item.month === month);
                    const settlement = activity?.monthly.find((item) => item.month === month);
                    return (
                      <div key={month} className="rounded-lg bg-surface-elev p-3">
                        <p className="text-sm font-medium text-fg-primary">{month}</p>
                        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          <span className="text-fg-tertiary">Sales revenue</span>
                          <span className="text-right tabular-nums">
                            {formatFinanceCurrency(sale?.revenueCents ?? null, currency)}
                          </span>
                          <span className="text-fg-tertiary">Settlement revenue</span>
                          <span className="text-right tabular-nums">
                            {formatFinanceDelta(
                              settlement?.settlementRevenueCents ?? null,
                              currency
                            )}
                          </span>
                          <span className="text-fg-tertiary">Amazon charges / credits</span>
                          <span className="text-right tabular-nums">
                            {formatFinanceDelta(settlement?.amazonNetChargeCents ?? null, currency)}
                          </span>
                          <span className="text-fg-tertiary">Landed COGS</span>
                          <span className="text-right tabular-nums">
                            {accountingMonthValue(settlement, 'landedCogsCents', currency)}
                          </span>
                          <span className="text-fg-tertiary">Warehouse expense</span>
                          <span className="text-right tabular-nums">
                            {accountingMonthValue(
                              settlement,
                              'warehousePeriodExpenseCents',
                              currency
                            )}
                          </span>
                          <span className="text-fg-tertiary">Net income</span>
                          <span className="text-right tabular-nums">
                            {accountingMonthValue(settlement, 'netIncomeCents', currency)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}

function accountingMonthValue(
  month: FinanceSummarySettlementActivity['monthly'][number] | undefined,
  key: AccountingMonthFactKey,
  currency: string
): string {
  return month ? accountingMonthFact(month, key, currency) : 'Unavailable';
}

function RecentTransactions() {
  return (
    <section aria-labelledby="reports-transactions-heading">
      <SectionHeading
        id="reports-transactions-heading"
        title="Recent transactions"
        detail="A unified transaction browser remains deferred so Fee Center stays the source-line detail view."
      />
      <Card>
        <div className="flex items-center gap-2">
          <Badge variant="neutral">Deferred</Badge>
          <p className="text-sm text-fg-secondary">
            Source-level transaction browsing is not duplicated here.
          </p>
        </div>
        <Link
          to="/app/finance/fee-center"
          className="inline-block mt-4 text-sm text-accent-fg hover:underline"
        >
          Open Fee Center for source detail
        </Link>
      </Card>
    </section>
  );
}

function ReportMonthRows<T extends { month: string }>({
  rows,
  columns,
  render,
}: {
  rows: T[];
  columns: string[];
  render: (row: T) => string[];
}) {
  return (
    <div className="mt-5">
      <div
        className="hidden sm:grid gap-3 py-2 text-xs text-fg-tertiary"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        {columns.map((column) => (
          <span key={column}>{column}</span>
        ))}
      </div>
      <div className="hidden sm:block divide-y divide-border-subtle">
        {rows.map((row) => (
          <div
            key={row.month}
            className="grid gap-3 py-2 text-sm"
            style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
          >
            {render(row).map((value, index) => (
              <span
                key={`${row.month}-${index}`}
                className={index > 0 ? 'tabular-nums' : 'text-fg-secondary'}
              >
                {value}
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className="sm:hidden space-y-2">
        {rows.map((row) => {
          const values = render(row);
          return (
            <div key={row.month} className="rounded-lg bg-surface-elev p-3">
              <p className="text-sm font-medium text-fg-primary">{values[0]}</p>
              <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                {columns.slice(1).map((column, index) => (
                  <span key={column} className="contents">
                    <span className="text-fg-tertiary">{column}</span>
                    <span className="text-right tabular-nums">{values[index + 1]}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CoverageBanner({ copy }: { copy: { title: string; detail: string } }) {
  return (
    <div className="border border-warning/30 bg-warning/[0.08] rounded-xl px-4 py-3">
      <p className="text-sm font-medium text-fg-primary">{copy.title}</p>
      <p className="text-xs text-fg-secondary mt-1">{copy.detail}</p>
    </div>
  );
}
function SectionHeading({ id, title, detail }: { id: string; title: string; detail: string }) {
  return (
    <div className="mb-3">
      <h2 id={id} className="text-lg font-medium text-fg-primary">
        {title}
      </h2>
      <p className="text-sm text-fg-secondary mt-1">{detail}</p>
    </div>
  );
}
function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-fg-tertiary">{label}</p>
      <p className="font-display text-2xl text-fg-primary mt-1 tabular-nums break-words">{value}</p>
    </div>
  );
}
function EmptyCard({ text, coverage = true }: { text: string; coverage?: boolean }) {
  return (
    <Card>
      <p className="text-sm text-fg-secondary">{text}</p>
      {!coverage && (
        <p className="text-xs text-fg-tertiary mt-1">
          Source coverage is incomplete; an empty result is not a zero.
        </p>
      )}
    </Card>
  );
}
function AccessLoading() {
  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16" aria-live="polite">
        <Card>
          <p className="text-sm text-fg-secondary">Checking reports access…</p>
        </Card>
      </main>
    </AppShell>
  );
}
function AccessDenied() {
  return (
    <AppShell>
      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16">
        <Card>
          <p className="text-sm text-fg-secondary">
            This account is not granted access to Reports.
          </p>
        </Card>
      </main>
    </AppShell>
  );
}
function AmazonDisabled() {
  return (
    <Card>
      <div className="flex items-center gap-2">
        <Badge variant="neutral">Amazon module off</Badge>
        <h2 className="text-sm font-medium text-fg-primary">Reports are waiting for Amazon data</h2>
      </div>
      <p className="text-sm text-fg-secondary mt-3">
        Connect the Amazon module to see sales, settlement activity, and payout evidence. No amounts
        are shown while the source is disabled.
      </p>
    </Card>
  );
}
